import { useEffect, useState } from "react";
import { Stack, Text, VStack, chakra } from "@chakra-ui/react";
import { RedButton } from "./RedButton"; // We'll create this next
import {
  KYDAdminEvent,
  KYDEventRefundExecution,
} from "@common/interfaces/KYDEvent"; // Adjust import path as needed
import { refundAbortStillOpen } from "./refundAbortWindow";

const useRefundAbortOpen = (
  execution: KYDEventRefundExecution | undefined
): boolean => {
  const waitUntilMs = execution?.wait_until
    ? Date.parse(execution.wait_until)
    : Number.NaN;
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (execution?.can_abort !== true || !Number.isFinite(waitUntilMs)) {
      return;
    }

    let timer = 0;
    const refreshAtDeadline = () => {
      const delay = waitUntilMs - Date.now();
      if (delay <= 0) {
        setNowMs((current) => (current >= waitUntilMs ? current : Date.now()));
        return;
      }
      timer = window.setTimeout(refreshAtDeadline, delay);
    };

    refreshAtDeadline();
    return () => window.clearTimeout(timer);
  }, [execution?.can_abort, waitUntilMs]);

  return refundAbortStillOpen(execution, nowMs);
};

const ColoredText = chakra.span;

interface DangerZoneProps {
  kydEvent: KYDAdminEvent;
  onArchive: () => void;
  onCancel: () => void;
  onDelete: () => void;
  onAbortRefund: () => void;
  loading: boolean;
}

export const getActionDetails = (
  action: string,
  kydEvent: KYDAdminEvent,
  handlers: {
    onArchive: () => void;
    onCancel: () => void;
    onDelete: () => void;
  }
) => {
  switch (action) {
    case "ARCHIVE":
      return {
        title: "Archive Event",
        buttonText: "Archive Event",
        onClick: handlers.onArchive,
        content: (
          <>
            <Text w="full">
              The event {kydEvent?.name} is currently{" "}
              <ColoredText color="green" fontWeight={"bold"} mr={1}>
                live
              </ColoredText>
              with{" "}
              <ColoredText color="green" fontWeight={"bold"}>
                {kydEvent?.total_tickets_purchased}
              </ColoredText>{" "}
              tickets sold.
            </Text>
            <Text w="full" fontSize="sm">
              Event will be moved to archive and not visible to fans
            </Text>
          </>
        ),
      };
    case "CANCEL": {
      const refundNotStarted = kydEvent?.status === "CANCELED";
      return {
        title: refundNotStarted ? "Start refunds" : "Cancel Event",
        buttonText: refundNotStarted ? "Start refunds" : "Cancel Event",
        onClick: handlers.onCancel,
        content: refundNotStarted ? (
          <Text w="full" fontSize="sm">
            {kydEvent?.name} is canceled, and customer refunds have not started.
          </Text>
        ) : (
          <>
            <Text w="full">
              The event {kydEvent?.name} is currently{" "}
              <ColoredText color="green" mr={1}>
                live
              </ColoredText>
              with{" "}
              <ColoredText color="green" fontWeight={"bold"}>
                {kydEvent?.total_tickets_purchased}
              </ColoredText>{" "}
              tickets sold.
            </Text>
            <Text w="full" fontSize="sm">
              This action results in{" "}
              <ColoredText fontWeight={"bold"} color="kydRed.200">
                cancellation of the event
              </ColoredText>
              . All customers will be automatically refunded within 5-7 business
              days.
            </Text>
          </>
        ),
      };
    }
    case "DELETE":
      return {
        title: "Delete Event",
        buttonText: "Delete Event",
        onClick: handlers.onDelete,
        content: (
          <>
            <Text w="full">
              The event {kydEvent?.name} is currently{" "}
              <ColoredText color="green" mr={1}>
                live
              </ColoredText>
              with no sales
            </Text>
            <Text w="full" fontSize="sm">
              This action results in{" "}
              <ColoredText fontWeight={"bold"} color="kydRed.200">
                permanent deletion
              </ColoredText>
              .
            </Text>
          </>
        ),
      };
    default:
      return null;
  }
};

export const DangerZone = ({
  kydEvent,
  onArchive,
  onCancel,
  onDelete,
  onAbortRefund,
  loading,
}: DangerZoneProps) => {
  const canAbortRefund = useRefundAbortOpen(kydEvent?.refund_execution);
  console.log("kydEvent", kydEvent?.status);
  if (kydEvent?.status === "ARCHIVED" || kydEvent?.status === "DELETED") {
    return null;
  }

  const deleteActions = kydEvent?.display_options?.delete_action ?? [];
  if (!canAbortRefund && deleteActions.length === 0) {
    return null;
  }

  return (
    <Stack>
      <Text fontWeight={"bold"} width={"full"}>
        Danger Zone
      </Text>
      <Text fontSize="md">
        {`This section is where you make changes to your event that can't be reversed, please proceed with caution`}
      </Text>
      <VStack
        borderWidth={1}
        borderColor="kydRed.200"
        borderRadius="md"
        w="full"
        p={4}
        gap={8}
      >
        {deleteActions.map((action: string) => {
          const actionDetails = getActionDetails(action, kydEvent, {
            onArchive,
            onCancel,
            onDelete,
          });
          if (!actionDetails) return null;

          return (
            <RedButton
              key={action}
              show={true}
              title={actionDetails.title}
              buttonText={actionDetails.buttonText}
              onClick={actionDetails.onClick}
              loading={loading}
            >
              {actionDetails.content}
            </RedButton>
          );
        })}
        {canAbortRefund ? (
          <RedButton
            show={true}
            title="Stop refunds"
            buttonText="Stop refunds"
            onClick={onAbortRefund}
            loading={loading}
          >
            <Text w="full" fontSize="sm">
              Customers have not been refunded yet. Stopping refunds leaves the
              event canceled.
            </Text>
          </RedButton>
        ) : null}
      </VStack>
    </Stack>
  );
};
